import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const THEMES = ["rose_elegant", "ivory_minimal", "luxury_gold", "olive_garden", "night_premium"];
const THEME_NAMES = {
  rose_elegant: "Rose Elegant",
  ivory_minimal: "Ivory Minimal",
  luxury_gold: "Luxury Gold",
  olive_garden: "Olive Garden",
  night_premium: "Night Premium",
};

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const target = new Date(targetIso).getTime();
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: diff === 0 };
}

function Section({ eyebrow, title, children, id, strong }) {
  return (
    <section id={id} className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-32">
      <div className="text-center mb-14 md:mb-20 max-w-[720px] mx-auto">
        {eyebrow && <div className="eyebrow mb-5">{eyebrow}</div>}
        {title && <h2 className={`h-section ${strong ? "h-section-strong" : ""}`}>{title}</h2>}
        <div className="divider-ornament mt-8"><span>✦</span></div>
      </div>
      {children}
    </section>
  );
}

function Countdown({ dateIso }) {
  const { d, h, m, s, done } = useCountdown(dateIso);
  if (done) return <div className="text-center font-serif-elegant text-4xl italic text-accent-brand">Bugün bizim günümüz.</div>;
  const cells = [["GÜN", d], ["SAAT", h], ["DAKİKA", m], ["SANİYE", s]];
  return (
    <div className="grid grid-cols-4 gap-3 md:gap-6 max-w-2xl mx-auto" data-testid="countdown">
      {cells.map(([label, val]) => (
        <div key={label} className="paper-card p-4 md:p-6 text-center">
          <div className="font-serif-elegant text-4xl md:text-5xl text-primary-brand" data-testid={`countdown-${label}`}>{String(val).padStart(2, "0")}</div>
          <div className="text-[0.65rem] md:text-xs tracking-luxury uppercase mt-2 text-secondary-brand">{label}</div>
        </div>
      ))}
    </div>
  );
}

function Lightbox({ items, index, onClose, onPrev, onNext }) {
  if (index == null) return null;
  const it = items[index];
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="lightbox">
      <button className="absolute top-6 right-6 text-white/80 text-3xl" onClick={onClose} data-testid="lightbox-close">×</button>
      <button className="absolute left-4 md:left-8 text-white/70 text-4xl" onClick={(e)=>{e.stopPropagation();onPrev();}} data-testid="lightbox-prev">‹</button>
      <button className="absolute right-4 md:right-8 text-white/70 text-4xl" onClick={(e)=>{e.stopPropagation();onNext();}} data-testid="lightbox-next">›</button>
      <img src={it.url} alt="" className="max-h-[90vh] max-w-[92vw] object-contain" onClick={(e)=>e.stopPropagation()} />
    </div>
  );
}

function HeroVideo({ src, poster, fallbackImage, onFail }) {
  const [ready, setReady] = useState(false);
  const posterUrl = poster || fallbackImage;
  return (
    <>
      {posterUrl && (
        <div className={`absolute inset-0 transition-opacity duration-1000 ${ready ? "opacity-0" : "opacity-100"}`}
          style={{ backgroundImage: `url(${posterUrl})`, backgroundSize: "cover", backgroundPosition: "center" }} data-testid="hero-video-poster" />
      )}
      <video autoPlay muted loop playsInline preload="metadata" poster={posterUrl || undefined}
        disablePictureInPicture controlsList="nodownload nofullscreen noremoteplayback"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${ready ? "opacity-100" : "opacity-0"}`}
        onCanPlay={() => setReady(true)} onError={onFail} data-testid="hero-video">
        <source src={src} type="video/mp4" />
      </video>
    </>
  );
}

function MusicPlayer({ src }) {
  const [playing, setPlaying] = useState(false);
  const ref = React.useRef(null);
  if (!src) return null;
  const toggle = () => {
    if (!ref.current) return;
    if (playing) { ref.current.pause(); setPlaying(false); }
    else { ref.current.play().then(()=>setPlaying(true)).catch(()=>{}); }
  };
  return (
    <>
      <audio ref={ref} src={src} loop />
      <button onClick={toggle} className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
        style={{ background: "var(--btn-bg)", color: "var(--btn-fg)" }} data-testid="music-toggle" aria-label="Müzik">
        <span className={playing ? "vinyl" : ""}>{playing ? "❚❚" : "♪"}</span>
      </button>
    </>
  );
}

export default function PublicInvitation() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [err, setErr] = useState(null);
  const [opened, setOpened] = useState(false);
  const [envelopeAnimating, setEnvelopeAnimating] = useState(false);
  const [lbIdx, setLbIdx] = useState(null);
  const [rsvp, setRsvp] = useState({ name: "", attending: true, guest_count: 1, note: "" });
  const [rsvpDone, setRsvpDone] = useState(false);
  const [memFile, setMemFile] = useState(null);
  const [memName, setMemName] = useState("");
  const [memNote, setMemNote] = useState("");
  const [memBusy, setMemBusy] = useState(false);
  const [memDone, setMemDone] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/wedding/${slug}`);
        setW(data);
        document.title = `${data.bride_name} & ${data.groom_name} — Düğün Davetiyesi`;
      } catch {
        setErr("Davetiye bulunamadı.");
      }
    })();
  }, [slug]);

  const openEnvelope = () => {
    setEnvelopeAnimating(true);
    setTimeout(() => setOpened(true), 1300);
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-brand text-secondary-brand">{err}</div>;
  if (!w) return <div className="min-h-screen flex items-center justify-center bg-brand text-secondary-brand">Yükleniyor…</div>;

  const dateIso = `${w.wedding_date}T${w.wedding_time || "19:30"}:00`;
  const dateObj = new Date(dateIso);
  const monthNames = ["OCAK","ŞUBAT","MART","NİSAN","MAYIS","HAZİRAN","TEMMUZ","AĞUSTOS","EYLÜL","EKİM","KASIM","ARALIK"];
  const dayFmt = `${dateObj.getDate()} ${monthNames[dateObj.getMonth()]} ${dateObj.getFullYear()}`;

  const submitRsvp = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/public/wedding/${slug}/rsvp`, rsvp);
      setRsvpDone(true);
      toast.success("Katılım bilginiz alındı. Teşekkür ederiz.");
    } catch {
      toast.error("Gönderilemedi. Lütfen tekrar deneyin.");
    }
  };

  const submitMemory = async (e) => {
    e.preventDefault();
    if (!memFile) return toast.error("Lütfen dosya seçin");
    setMemBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", memFile);
      fd.append("guest_name", memName);
      fd.append("note", memNote);
      await api.post(`/public/wedding/${slug}/memory`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMemDone(true);
      toast.success("Anınız alındı, onay sonrası gösterilecek.");
    } catch {
      toast.error("Yükleme başarısız");
    } finally { setMemBusy(false); }
  };

  const videoSrc = w.hero_video_url || w.hero_video;
  const useVideo = !!(w.hero_video_enabled && videoSrc && !videoFailed);
  const publicUrl = window.location.href;
  const shareText = `${w.bride_name} & ${w.groom_name} — Düğün Davetiyesi`;

  return (
    <div lang="tr" data-theme={w.theme || "rose_elegant"} className="bg-brand text-primary-brand min-h-screen">
      {/* Envelope Gate */}
      {!opened && (
        <div className={`fixed inset-0 z-[80] ${envelopeAnimating ? "envelope-out" : ""}`} style={{ background: "var(--bg)" }}>
          <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,${w.overlay_opacity || 0.35}), rgba(0,0,0,${w.overlay_opacity || 0.35})), url(${w.cover_image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 text-white">
            <div className="reveal hero-label" style={{ color: "#EDE0D2" }}>— Düğün Davetiyesi —</div>
            <h1 className="h-names mt-8 reveal reveal-delay-1">{w.bride_name}</h1>
            <div className="h-amp my-3 md:my-4 reveal reveal-delay-1" style={{ color: "#EDE0D2" }}>&</div>
            <h1 className="h-names reveal reveal-delay-2">{w.groom_name}</h1>
            <p className="mt-10 hero-label reveal reveal-delay-3">{w.invitation_message || "DÜĞÜNÜMÜZE DAVETLİSİNİZ"}</p>
            <p className="mt-3 hero-date reveal reveal-delay-3">{dayFmt} · {w.wedding_time}</p>
            <button onClick={openEnvelope} className="mt-12 soft-pulse reveal reveal-delay-4" style={{ background: "transparent", color: "#fff", border: "1px solid #fff", padding: "1rem 3rem", letterSpacing: "0.32em", fontSize: "0.75rem", textTransform: "uppercase" }} data-testid="open-envelope-btn">
              Davetiyeni Aç
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className={`relative hero-full flex ${useVideo ? "items-end pb-24 md:pb-28" : "items-center"} justify-center text-center overflow-hidden`} data-testid={useVideo ? "hero-video-section" : "hero-image-section"}>
        {useVideo ? (
          <HeroVideo src={videoSrc} poster={w.hero_video_poster} fallbackImage={w.cover_image} onFail={() => setVideoFailed(true)} />
        ) : (
          <div className="absolute inset-0" style={{ backgroundImage: `url(${w.cover_image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}
        {useVideo
          ? <div className="absolute inset-0 hero-video-shade" />
          : <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${w.overlay_opacity || 0.35})` }} />}
        {useVideo && (
          <a href="#story" className="scroll-cue" data-testid="hero-scroll-cue">
            <span>Keşfet</span>
            <span className="scroll-cue-arrow">↓</span>
          </a>
        )}
        <div className="relative z-10 px-6 w-full max-w-4xl text-white" data-testid="hero">
          <div className="hero-label" style={{ color: "#EDE0D2" }} data-testid="hero-label">DÜĞÜNÜMÜZE DAVETLİSİNİZ</div>
          <h1 className="h-names mt-8" data-testid="hero-bride">{w.bride_name}</h1>
          <div className="h-amp my-3 md:my-4" style={{ color: "#EDE0D2" }}>&</div>
          <h1 className="h-names" data-testid="hero-groom">{w.groom_name}</h1>
          <p className="mt-10 hero-date" data-testid="hero-date">{dayFmt} · {w.wedding_time}</p>
        </div>
      </section>

      {/* Editorial transition */}
      <div className="hero-transition" aria-hidden="true">
        <div className="line"></div>
        <span className="font-serif-elegant italic text-xl md:text-2xl">{w.bride_name} & {w.groom_name}</span>
      </div>

      {/* Intro */}
      {w.intro_text && (
        <Section eyebrow="— BİZE DAİR —" title={<>Birlikte başlayan<br />güzel bir hikâye</>} id="story">
          <p className="body-copy text-center max-w-[650px] mx-auto" data-testid="intro-text">{w.intro_text}</p>
        </Section>
      )}

      {/* Countdown */}
      <Section eyebrow="— Geri Sayım —" title="Özel Günümüz">
        <Countdown dateIso={dateIso} />
      </Section>

      {/* Wedding Info */}
      <Section eyebrow="— Detaylar —" title="Düğün Bilgileri" id="details">
        <div className="paper-card p-8 md:p-12 max-w-2xl mx-auto text-center">
          <div className="eyebrow">Tarih</div>
          <div className="font-serif-elegant text-3xl mt-2">{dayFmt}</div>
          <div className="mt-1 body-copy text-secondary-brand">{w.wedding_time}</div>
          <div className="hairline my-8"></div>
          <div className="eyebrow">Mekân</div>
          <div className="font-serif-elegant text-3xl mt-2">{w.venue}</div>
          <div className="mt-1 body-copy text-secondary-brand">{w.address}</div>
          {w.maps_url && (
            <a href={w.maps_url} target="_blank" rel="noreferrer" className="btn-primary mt-8" data-testid="directions-btn">Yol Tarifi</a>
          )}
        </div>
      </Section>

      {/* Program */}
      {w.program?.length > 0 && (
        <Section eyebrow="— Akış —" title="Düğün Programı">
          <ol className="max-w-2xl mx-auto space-y-6" data-testid="program-list">
            {w.program.map((p) => (
              <li key={p.id} className="flex items-baseline gap-6 border-b border-brand pb-6">
                <div className="font-serif-elegant text-3xl text-accent-brand w-24">{p.time}</div>
                <div className="font-serif-elegant text-xl">{p.title}</div>
              </li>
            ))}
          </ol>
          {w.program_note?.trim() && (
            <div className="max-w-2xl mx-auto mt-8 text-center space-y-1.5" data-testid="program-note">
              {w.program_note.split("\n").filter(l => l.trim()).map((line, i) => (
                <div key={i} className="text-xs md:text-[0.8rem] tracking-wide text-secondary-brand">{line}</div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Our Story */}
      {w.story?.length > 0 && (
        <Section eyebrow="— Bizim Hikâyemiz —" title="Zaman Çizelgesi">
          <div className="max-w-3xl mx-auto space-y-10">
            {w.story.map((s, i) => (
              <div key={s.id} className={`flex flex-col md:flex-row gap-6 ${i%2 ? "md:flex-row-reverse" : ""}`}>
                {s.image_url && <img src={s.image_url} alt="" className="md:w-1/3 object-cover" />}
                <div className="flex-1">
                  <div className="eyebrow">{s.year}</div>
                  <h3 className="font-serif-elegant text-3xl mt-2">{s.title}</h3>
                  <p className="mt-3 body-copy">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Gallery */}
      {w.gallery?.length > 0 && (
        <Section eyebrow="— Anlar —" title="Fotoğraflarımız">
          <div className="masonry" data-testid="gallery">
            {w.gallery.map((g, i) => (
              <img key={g.id} src={g.url} alt="" className="w-full cursor-zoom-in hover:opacity-90 transition" loading="lazy"
                onClick={() => setLbIdx(i)} data-testid={`gallery-item-${i}`} />
            ))}
          </div>
          <Lightbox items={w.gallery} index={lbIdx} onClose={() => setLbIdx(null)}
            onPrev={() => setLbIdx((lbIdx - 1 + w.gallery.length) % w.gallery.length)}
            onNext={() => setLbIdx((lbIdx + 1) % w.gallery.length)} />
        </Section>
      )}

      {/* RSVP */}
      {w.rsvp_visible !== false && (
      <Section eyebrow="— KATILIM BİLDİRİMİ —" title={<>Düğüne katılıyor<br />musunuz?</>} strong id="rsvp">
        {rsvpDone ? (
          <div className="paper-card p-10 max-w-lg mx-auto text-center" data-testid="rsvp-success">
            <div className="font-serif-elegant text-3xl mb-3">Teşekkür ederiz</div>
            <p className="body-copy">Katılım bilginiz alındı. Sizi görmek için sabırsızlanıyoruz.</p>
          </div>
        ) : (
          <form onSubmit={submitRsvp} className="paper-card p-8 md:p-10 max-w-xl mx-auto space-y-6" data-testid="rsvp-form">
            <label className="block">
              <div className="text-xs tracking-luxury uppercase text-secondary-brand mb-2">Ad Soyad</div>
              <input required value={rsvp.name} onChange={e=>setRsvp({...rsvp, name: e.target.value})}
                className="w-full bg-transparent border-b border-brand py-2 outline-none focus:border-accent-brand"
                data-testid="rsvp-name" />
            </label>
            <div>
              <div className="text-xs tracking-luxury uppercase text-secondary-brand mb-2">Katılım Durumu</div>
              <div className="flex gap-3">
                <button type="button" onClick={()=>setRsvp({...rsvp, attending: true})}
                  className={`flex-1 py-3 text-xs tracking-luxury uppercase border ${rsvp.attending ? "text-white" : "text-secondary-brand"}`}
                  style={rsvp.attending ? {background:"var(--btn-bg)", borderColor:"var(--btn-bg)"} : {borderColor:"var(--border)"}}
                  data-testid="rsvp-attending-yes">Katılacağım</button>
                <button type="button" onClick={()=>setRsvp({...rsvp, attending: false})}
                  className={`flex-1 py-3 text-xs tracking-luxury uppercase border ${!rsvp.attending ? "text-white" : "text-secondary-brand"}`}
                  style={!rsvp.attending ? {background:"var(--btn-bg)", borderColor:"var(--btn-bg)"} : {borderColor:"var(--border)"}}
                  data-testid="rsvp-attending-no">Katılamayacağım</button>
              </div>
            </div>
            {rsvp.attending && (
              <label className="block">
                <div className="text-xs tracking-luxury uppercase text-secondary-brand mb-2">Kişi Sayısı</div>
                <select value={rsvp.guest_count} onChange={e=>setRsvp({...rsvp, guest_count: parseInt(e.target.value)})}
                  className="w-full bg-transparent border-b border-brand py-2 outline-none focus:border-accent-brand"
                  data-testid="rsvp-guest-count">
                  {Array.from({length:20}).map((_,i)=><option key={i} value={i+1}>{i+1}</option>)}
                </select>
              </label>
            )}
            <label className="block">
              <div className="text-xs tracking-luxury uppercase text-secondary-brand mb-2">Not (isteğe bağlı)</div>
              <textarea rows={3} value={rsvp.note} onChange={e=>setRsvp({...rsvp, note: e.target.value})}
                className="w-full bg-transparent border border-brand p-3 outline-none focus:border-accent-brand resize-none"
                data-testid="rsvp-note" />
            </label>
            <button className="btn-primary w-full justify-center" data-testid="rsvp-submit">Bilgilerimi Gönder</button>
          </form>
        )}
      </Section>
      )}

      {/* Guest Memory Upload */}
      <Section eyebrow="— KONUKLARDAN —" title={<>Anılarınızı<br />bizimle paylaşın</>}>
        {memDone ? (
          <div className="paper-card p-10 max-w-lg mx-auto text-center" data-testid="memory-success">
            <div className="font-serif-elegant text-2xl mb-2">Anınız alındı</div>
            <p className="text-secondary-brand text-sm">Onaylandıktan sonra galeride görüntülenecektir.</p>
          </div>
        ) : (
          <form onSubmit={submitMemory} className="paper-card p-8 md:p-10 max-w-xl mx-auto space-y-5" data-testid="memory-form">
            <label className="block">
              <div className="text-xs tracking-luxury uppercase text-secondary-brand mb-2">Adınız (opsiyonel)</div>
              <input value={memName} onChange={e=>setMemName(e.target.value)}
                className="w-full bg-transparent border-b border-brand py-2 outline-none focus:border-accent-brand" data-testid="memory-name" />
            </label>
            <label className="block">
              <div className="text-xs tracking-luxury uppercase text-secondary-brand mb-2">Fotoğraf / Video</div>
              <input type="file" required accept="image/*,video/*" onChange={e=>setMemFile(e.target.files?.[0])}
                className="w-full text-sm" data-testid="memory-file" />
            </label>
            <label className="block">
              <div className="text-xs tracking-luxury uppercase text-secondary-brand mb-2">Mesajınız</div>
              <textarea rows={3} value={memNote} onChange={e=>setMemNote(e.target.value)}
                className="w-full bg-transparent border border-brand p-3 outline-none focus:border-accent-brand resize-none"
                data-testid="memory-note" />
            </label>
            <button disabled={memBusy} className="btn-primary w-full justify-center" data-testid="memory-submit">
              {memBusy ? "Yükleniyor..." : "Gönder"}
            </button>
          </form>
        )}
      </Section>

      {/* Approved memories */}
      {w.approved_memories?.length > 0 && (
        <Section eyebrow="— Düğünden —" title="Anılar">
          <div className="masonry">
            {w.approved_memories.map((m) => (
              <div key={m.id} className="paper-card overflow-hidden">
                {m.media_kind === "video"
                  ? <video src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${m.id}`} controls className="w-full" />
                  : <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${m.id}`} alt="" className="w-full" />}
                {m.note && <div className="p-4 text-sm text-secondary-brand italic">"{m.note}" — {m.guest_name || "Bir konuk"}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Share */}
      <Section eyebrow="— Paylaş —" title="Sevdiklerinizle Paylaşın">
        <div className="flex flex-wrap justify-center gap-3" data-testid="share-buttons">
          <a href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + publicUrl)}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="share-whatsapp">WhatsApp</a>
          <a href={`https://t.me/share/url?url=${encodeURIComponent(publicUrl)}&text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="share-telegram">Telegram</a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="share-facebook">Facebook</a>
          <button className="btn-ghost" onClick={()=>{navigator.clipboard.writeText(publicUrl); toast.success("Bağlantı kopyalandı");}} data-testid="share-copy">Bağlantıyı Kopyala</button>
        </div>
      </Section>

      <footer className="border-t border-brand py-8 text-center text-xs tracking-luxury text-secondary-brand">
        <Link to="/" className="hover:text-accent-brand">BiGeldi Dijital Davetiye</Link>
      </footer>

      <MusicPlayer src={w.music_url} />
    </div>
  );
}
